"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import FilterSection from "@/components/filter-panel/Section";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Filter } from "lucide-react";
import { UniversalFilterProps, FilterValueMap } from "./types";
import { findItemInfo, getAllDescendantIds, findParentOption, expandSelection, compactSelection } from "./utils/tree-helpers";
import { cleanValue, getPathFromUrl, extractCleanCategoryId, computeIsNavbarLabel } from "./utils/url-helpers";
const UniversalFilterComponent = t0 => {
  const $ = _c(76);
  const {
    filterSchema,
    onClearFilters,
    onClose,
    searchParams: searchParamsProp,
    parentCategoryUrl,
    parentItemId,
    isCollection,
    isNavbarLabel
  } = t0;
  const router = useRouter();
  let params;
  if ($[0] !== searchParamsProp) {
    params = new URLSearchParams();
    if (searchParamsProp) {
      Object.entries(searchParamsProp).forEach(t1 => {
        const [key, value] = t1;
        if (value !== undefined) {
          const stringValue = Array.isArray(value) ? value[0] ?? "" : value;
          params.set(key, stringValue);
        }
      });
    }
    $[0] = searchParamsProp;
    $[1] = params;
  } else {
    params = $[1];
  }
  const searchParams = params;
  let t1;
  if ($[2] !== isNavbarLabel || $[3] !== parentCategoryUrl || $[4] !== parentItemId) {
    t1 = computeIsNavbarLabel(isNavbarLabel, parentItemId, parentCategoryUrl);
    $[2] = isNavbarLabel;
    $[3] = parentCategoryUrl;
    $[4] = parentItemId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const computedIsNavbarLabel = t1;
  let t2;
  if ($[6] !== parentCategoryUrl || $[7] !== parentItemId) {
    t2 = extractCleanCategoryId(parentItemId, parentCategoryUrl);
    $[6] = parentCategoryUrl;
    $[7] = parentItemId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const parentCleanId = t2;
  const isInitialized = useRef(false);
  const isInternalUpdate = useRef(false);
  const isUserInteracting = useRef(false);
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const [filters, setFilters] = useState(t3);
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const [debouncedFilters, setDebouncedFilters] = useState(t4);
  let t5;
  if ($[11] !== onClearFilters || $[12] !== router) {
    t5 = () => {
      if (onClearFilters) {
        onClearFilters();
      } else {
        const currentParams = new URLSearchParams(window.location.search);
        const paramsToKeep = new URLSearchParams();
        if (currentParams.has("id")) {
          paramsToKeep.set("id", currentParams.get("id"));
        }
        if (currentParams.has("category_id")) {
          paramsToKeep.set("category_id", currentParams.get("category_id"));
        }
        const newSearch = paramsToKeep.toString();
        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
        window.dispatchEvent(new Event("route-change-start"));
        router.replace(newUrl, {
          scroll: false
        });
      }
      setFilters(_temp);
      setDebouncedFilters(_temp2);
      isUserInteracting.current = true;
    };
    $[11] = onClearFilters;
    $[12] = router;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleClearFilters = t5;
  let t6;
  let t7;
  if ($[14] !== filters) {
    t6 = () => {
      const timer = setTimeout(() => {
        setDebouncedFilters(filters);
      }, 400);
      return () => clearTimeout(timer);
    };
    t7 = [filters];
    $[14] = filters;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[17] !== filterSchema || $[18] !== searchParams) {
    t8 = () => {
      const initialFilters = {};
      filterSchema.forEach(field => {
        const param = searchParams.get(field.id);
        if (param !== null) {
          if (field.type === "price" || field.type === "range") {
            const vals = param.split("-").map(Number).filter(_temp3);
            if (vals.length === 2) {
              initialFilters[field.id] = vals;
            }
          } else {
            if (field.type === "checkbox") {
              const vals_0 = param.split(",").map(_temp4).filter(Boolean);
              if (vals_0.length > 0) {
                initialFilters[field.id] = expandSelection(field, vals_0);
              }
            } else {
              const val = decodeURIComponent(cleanValue(param));
              if (val) {
                initialFilters[field.id] = val;
              }
            }
          }
        }
      });
      setFilters(prev_1 => {
        if (isInternalUpdate.current) {
          isInternalUpdate.current = false;
          return prev_1;
        }
        const isDifferent = JSON.stringify(prev_1) !== JSON.stringify(initialFilters);
        if (isDifferent) {
          isUserInteracting.current = false;
          return initialFilters;
        }
        return prev_1;
      });
      isInitialized.current = true;
    };
    t9 = [searchParams, filterSchema];
    $[17] = filterSchema;
    $[18] = searchParams;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] !== computedIsNavbarLabel || $[22] !== debouncedFilters || $[23] !== filterSchema || $[24] !== filters || $[25] !== isCollection || $[26] !== onClose || $[27] !== parentCategoryUrl || $[28] !== parentCleanId || $[29] !== parentItemId || $[30] !== router || $[31] !== searchParams) {
    t10 = () => {
      if (!isInitialized.current) {
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString());
      let hasChanged = false;
      const filtersToSync = onClose ? filters : debouncedFilters;
      for (const field_0 of filterSchema) {
        const key_0 = field_0.id;
        const value_0 = filtersToSync[key_0];
        if (Array.isArray(value_0)) {
          if (typeof value_0[0] === "number") {
            const paramValue = value_0.join("-");
            if (params_0.get(key_0) !== paramValue) {
              params_0.set(key_0, paramValue);
              hasChanged = true;
            }
          } else {
            const compacted = compactSelection(field_0, value_0);
            const cleaned = compacted.map(_temp5).filter(Boolean);
            if (cleaned.length > 0) {
              const paramValue_0 = cleaned.map(encodeURIComponent).join(",");
              if (params_0.get(key_0) !== paramValue_0) {
                params_0.set(key_0, paramValue_0);
                hasChanged = true;
              }
            } else {
              if (params_0.has(key_0)) {
                params_0.delete(key_0);
                hasChanged = true;
              }
            }
          }
        } else {
          if (value_0 !== undefined && value_0 !== "") {
            const cleanedVal = encodeURIComponent(cleanValue(value_0));
            if (params_0.get(key_0) !== cleanedVal) {
              params_0.set(key_0, cleanedVal);
              hasChanged = true;
            }
          } else {
            if (params_0.has(key_0)) {
              params_0.delete(key_0);
              hasChanged = true;
            }
          }
        }
      }
      let idValue = params_0.get("id");
      const originalCategoryId = searchParams.get("category_id");
      const idField = filterSchema.find(_temp6);
      if (idField && idField.options && filtersToSync.id) {
        const selectedIds = Array.isArray(filtersToSync.id) ? filtersToSync.id.map(String) : [String(filtersToSync.id)];
        const allTopLevelIds = idField.options.map(_temp7);
        const areAllSelected = allTopLevelIds.length > 0 && allTopLevelIds.every(id => selectedIds.includes(id));
        if (areAllSelected) {
          if (isCollection && parentItemId) {
            params_0.set("id", parentItemId);
            params_0.delete("category_id");
            idValue = parentItemId;
            hasChanged = true;
          } else {
            if (computedIsNavbarLabel && parentCleanId) {
              params_0.set("category_id", parentCleanId);
              params_0.delete("id");
              idValue = null;
              hasChanged = true;
            } else {
              if (originalCategoryId) {
                params_0.set("category_id", String(originalCategoryId));
                params_0.delete("id");
                idValue = null;
                hasChanged = true;
              }
            }
          }
        }
      }
      if (idValue && originalCategoryId && idValue === String(originalCategoryId)) {
        params_0.set("category_id", String(originalCategoryId));
        params_0.delete("id");
        idValue = null;
        hasChanged = true;
      }
      if (idValue) {
        if (params_0.has("category_id")) {
          params_0.delete("category_id");
          hasChanged = true;
        }
      } else {
        if (computedIsNavbarLabel && parentCleanId) {
          if (params_0.get("category_id") !== parentCleanId || params_0.has("id")) {
            params_0.set("category_id", parentCleanId);
            params_0.delete("id");
            hasChanged = true;
          }
        } else {
          if (originalCategoryId) {
            if (params_0.get("category_id") !== String(originalCategoryId) || params_0.has("id")) {
              params_0.set("category_id", String(originalCategoryId));
              params_0.delete("id");
              hasChanged = true;
            }
          } else {
            if (parentCleanId && !isCollection) {
              if (params_0.get("id") !== parentCleanId || params_0.has("category_id")) {
                params_0.set("id", parentCleanId);
                params_0.delete("category_id");
                hasChanged = true;
              }
            }
          }
        }
      }
      if (params_0.has("parent_nav_collection_id")) {
        params_0.delete("parent_nav_collection_id");
        hasChanged = true;
      }
      let targetPath = window.location.pathname;
      if (idField && idField.options) {
        const selectedIds_0 = idValue ? idValue.split(",").map(_temp8).filter(Boolean) : [];
        if (selectedIds_0.length === 1) {
          const targetId = selectedIds_0[0];
          if (targetId) {
            const optionInfo = findItemInfo(idField.options, targetId);
            if (optionInfo?.item?.url) {
              const path = getPathFromUrl(optionInfo.item.url);
              if (path) {
                targetPath = path;
              }
            } else {
              if (parentCategoryUrl) {
                const path_0 = getPathFromUrl(parentCategoryUrl);
                if (path_0) {
                  targetPath = path_0;
                }
              }
            }
          }
        } else {
          if (selectedIds_0.length > 1) {
            const targetId_0 = selectedIds_0[0];
            if (targetId_0) {
              const parentOption = findParentOption(idField.options, targetId_0);
              if (parentOption?.url) {
                const path_1 = getPathFromUrl(parentOption.url);
                if (path_1) {
                  targetPath = path_1;
                }
              } else {
                if (parentCategoryUrl) {
                  const path_2 = getPathFromUrl(parentCategoryUrl);
                  if (path_2) {
                    targetPath = path_2;
                  }
                }
              }
            }
          } else {
            if (parentCategoryUrl) {
              const path_3 = getPathFromUrl(parentCategoryUrl);
              if (path_3) {
                targetPath = path_3;
              }
            }
          }
        }
      } else {
        if (parentCategoryUrl && !idValue) {
          const path_4 = getPathFromUrl(parentCategoryUrl);
          if (path_4) {
            targetPath = path_4;
          }
        }
      }
      if (targetPath !== window.location.pathname) {
        hasChanged = true;
      }
      if (hasChanged) {
        isInternalUpdate.current = true;
        isUserInteracting.current = false;
        const newParams = params_0.toString();
        window.dispatchEvent(new Event("route-change-start"));
        router.push(`${targetPath}?${newParams}`, {
          scroll: false
        });
        if (!onClose) {
          const listingElement = document.getElementById("listing-section");
          if (listingElement) {
            const elementPosition = listingElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 200;
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        }
      }
    };
    $[21] = computedIsNavbarLabel;
    $[22] = debouncedFilters;
    $[23] = filterSchema;
    $[24] = filters;
    $[25] = isCollection;
    $[26] = onClose;
    $[27] = parentCategoryUrl;
    $[28] = parentCleanId;
    $[29] = parentItemId;
    $[30] = router;
    $[31] = searchParams;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const syncToUrl = t10;
  let t11;
  if ($[33] !== onClose || $[34] !== syncToUrl) {
    t11 = () => {
      if (!isInitialized.current || !isUserInteracting.current) {
        return;
      }
      if (onClose) {
        return;
      }
      syncToUrl();
    };
    $[33] = onClose;
    $[34] = syncToUrl;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== debouncedFilters || $[37] !== onClose || $[38] !== syncToUrl) {
    t12 = [debouncedFilters, syncToUrl, onClose];
    $[36] = debouncedFilters;
    $[37] = onClose;
    $[38] = syncToUrl;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  useEffect(t11, t12);
  let handleShowResults;
  let t13;
  let t14;
  let t15;
  let t16;
  if ($[40] !== filterSchema || $[41] !== filters || $[42] !== handleClearFilters || $[43] !== onClose || $[44] !== syncToUrl) {
    const handleFilterChange = (fieldId, value_1) => {
      isUserInteracting.current = true;
      const field_1 = filterSchema.find(f_0 => f_0.id === fieldId);
      setFilters(prev_2 => {
        const currentRaw = prev_2[fieldId] ?? [];
        const current = Array.isArray(currentRaw) ? currentRaw.map(String) : [String(currentRaw)];
        const cleanedValue = String(cleanValue(value_1));
        const isCurrentlySelected = current.includes(cleanedValue);
        if (field_1?.type === "checkbox") {
          const updatedSet = new Set(current);
          const itemInfo = findItemInfo(field_1.options || [], cleanedValue);
          if (!isCurrentlySelected) {
            updatedSet.add(cleanedValue);
            if (itemInfo?.item.children || itemInfo?.item.subTypes) {
              getAllDescendantIds(itemInfo.item).forEach(id_0 => updatedSet.add(String(id_0)));
            }
          } else {
            updatedSet.delete(cleanedValue);
            if (itemInfo?.item.children || itemInfo?.item.subTypes) {
              getAllDescendantIds(itemInfo.item).forEach(id_1 => updatedSet.delete(String(id_1)));
            }
            if (itemInfo?.parentId) {
              let currParentId = itemInfo.parentId;
              while (currParentId) {
                updatedSet.delete(String(currParentId));
                const parentInfo = findItemInfo(field_1.options || [], currParentId);
                currParentId = parentInfo?.parentId ?? null;
              }
            }
          }
          const expanded = expandSelection(field_1, Array.from(updatedSet));
          return {
            ...prev_2,
            [fieldId]: expanded
          };
        }
        const finalValue = value_1 === "" || value_1 === null || value_1 === undefined ? undefined : value_1;
        if (finalValue === undefined) {
          const newFilters = {
            ...prev_2
          };
          delete newFilters[fieldId];
          return newFilters;
        }
        return {
          ...prev_2,
          [fieldId]: finalValue
        };
      });
      isInternalUpdate.current = true;
    };
    let t17;
    if ($[50] !== filters) {
      t17 = Object.keys(filters);
      $[50] = filters;
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    const hasActiveFilters = t17.length > 0;
    let t18;
    if ($[52] !== onClose || $[53] !== syncToUrl) {
      t18 = () => {
        isUserInteracting.current = true;
        syncToUrl();
        if (onClose) {
          onClose();
        }
      };
      $[52] = onClose;
      $[53] = syncToUrl;
      $[54] = t18;
    } else {
      t18 = $[54];
    }
    handleShowResults = t18;
    t15 = "flex flex-col h-full bg-white lg:bg-stone-50 lg:p-4 w-full";
    let t19;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-[#037a6a]/10 flex items-center justify-center text-[#037a6a]"><Filter size={16} /></div><h2 className="font-outfit uppercase font-semibold text-lg text-gray-900 tracking-tight">Filters</h2></div>;
      $[55] = t19;
    } else {
      t19 = $[55];
    }
    let t20;
    if ($[56] !== handleClearFilters || $[57] !== hasActiveFilters) {
      t20 = hasActiveFilters && <motion.button initial={{
        opacity: 0,
        x: 10
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 10
      }} onClick={handleClearFilters} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-200 text-stone-600 hover:bg-stone-300 hover:text-stone-800 transition-all text-[10px] font-bold uppercase tracking-wider"><RotateCcw size={12} />Clear</motion.button>;
      $[56] = handleClearFilters;
      $[57] = hasActiveFilters;
      $[58] = t20;
    } else {
      t20 = $[58];
    }
    if ($[59] !== t20) {
      t16 = <div className="sticky top-0 z-20 px-4 lg:px-0 py-3 lg:py-4 bg-white lg:bg-stone-50 border-b border-stone-100 lg:mb-8 mb-4 flex items-center justify-between">{t19}<AnimatePresence>{t20}</AnimatePresence></div>;
      $[59] = t20;
      $[60] = t16;
    } else {
      t16 = $[60];
    }
    t13 = "flex-1 overflow-y-auto overflow-x-hidden no-scrollbar scrollbar-hide px-4 lg:px-0 space-y-4 pb-32 lg:pb-10";
    t14 = filterSchema.map((field_2, index) => <motion.div key={field_2.id} initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.05
    }}><FilterSection field={field_2} value={filters[field_2.id]} onChange={val_0 => handleFilterChange(field_2.id, val_0)} /></motion.div>);
    $[40] = filterSchema;
    $[41] = filters;
    $[42] = handleClearFilters;
    $[43] = onClose;
    $[44] = syncToUrl;
    $[45] = handleShowResults;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
  } else {
    handleShowResults = $[45];
    t13 = $[46];
    t14 = $[47];
    t15 = $[48];
    t16 = $[49];
  }
  let t17;
  if ($[61] !== t13 || $[62] !== t14) {
    t17 = <div className={t13}>{t14}</div>;
    $[61] = t13;
    $[62] = t14;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== handleClearFilters) {
    t18 = <Button variant="outline" onClick={handleClearFilters} className="flex-1 py-6 rounded-xl border-stone-200 text-stone-600 font-bold font-outfit text-[13px] uppercase tracking-wider">Reset All</Button>;
    $[64] = handleClearFilters;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== handleShowResults) {
    t19 = <Button onClick={handleShowResults} className="flex-1 py-6 bg-[#037a6a] hover:bg-[#026658] text-white rounded-xl font-bold font-outfit text-[13px] uppercase tracking-wider shadow-lg shadow-[#037a6a]/20">Show Results</Button>;
    $[66] = handleShowResults;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t18 || $[69] !== t19) {
    t20 = <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-lg border-t border-gray-100 flex gap-3 z-50">{t18}{t19}</div>;
    $[68] = t18;
    $[69] = t19;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] !== t15 || $[72] !== t16 || $[73] !== t17 || $[74] !== t20) {
    t21 = <div className={t15}>{t16}{t17}{t20}</div>;
    $[71] = t15;
    $[72] = t16;
    $[73] = t17;
    $[74] = t20;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  return t21;
};
export default UniversalFilterComponent;
function _temp(prev) {
  const preserved = {};
  if (prev.id !== undefined) {
    preserved.id = prev.id;
  }
  if (prev.category_id !== undefined) {
    preserved.category_id = prev.category_id;
  }
  return preserved;
}
function _temp2(prev_0) {
  const preserved_0 = {};
  if (prev_0.id !== undefined) {
    preserved_0.id = prev_0.id;
  }
  if (prev_0.category_id !== undefined) {
    preserved_0.category_id = prev_0.category_id;
  }
  return preserved_0;
}
function _temp3(n) {
  return !isNaN(n);
}
function _temp4(v) {
  return decodeURIComponent(cleanValue(v));
}
function _temp5(v_0) {
  return cleanValue(v_0);
}
function _temp6(f) {
  return f.id === "id";
}
function _temp7(opt) {
  return String(opt.id);
}
function _temp8(s) {
  return s.trim();
}